import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { Subscription } from 'rxjs';
import { v4 } from 'uuid';
import moment from 'moment';
import {
  AzureStorageDocument,
  UploadedDocumentRef,
  countryOfCitizenshipByName,
  IndividualFormValue,
  KybManualPerson,
  PropertyOwnerWithAddressValue,
} from '@portal-workspace/grow-shared-library';
import { IndividualIdDocumentUpload, individualIdDocumentsOnFile } from '../component-utils';
import { IndividualFormComponent } from '../individual-form-component/individual-form.component';
import { MessageBoxComponent } from '../message-box/message-box.component';

interface ManualPersonRow {
  clientId: string;
  individualId: number | null;
  applicationIndividualRoleId: number | null;
  control: FormControl<IndividualFormValue>;
  isSigner: boolean;
  isGuarantor: boolean;
  idDocumentProvided: boolean;
  uploadedIdDocuments: AzureStorageDocument[];
  subscription: Subscription;
}

@Component({
  selector: 'manual-kyb-people',
  standalone: true,
  templateUrl: './manual-kyb-people.component.html',
  styleUrls: ['./manual-kyb-people.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCheckboxModule,
    IndividualFormComponent,
    MessageBoxComponent,
  ],
})
export class ManualKybPeopleComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) commercialEntityId!: number;
  @Input() initialPeople: KybManualPerson[] = [];
  @Input() uploadedDocuments: AzureStorageDocument[] | null = null;
  @Input() showWaitingMessage = true;
  @Input() maxPeople: number | null = null;
  // Sole trader: every person is a guarantor and a signer, and the ticks cannot be changed.
  @Input() lockRoles = false;
  @Output() peopleChange = new EventEmitter<KybManualPerson[]>();
  @Output() validChange = new EventEmitter<boolean>();
  @Output() idDocumentDelete = new EventEmitter<UploadedDocumentRef>();

  rows: ManualPersonRow[] = [];
  showValidation = false;
  private hydratedForEntityId: number | null = null;

  constructor(private readonly formBuilder: FormBuilder) {}

  get hasSigner(): boolean {
    return this.rows.some(row => row.isSigner);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const shouldHydrate =
      changes['commercialEntityId']
      || (changes['initialPeople'] && (
        this.hydratedForEntityId !== this.commercialEntityId
        || (!this.rows.length && !!this.initialPeople?.length)
      ));
    if (shouldHydrate) {
      this.hydrate();
    } else if (changes['uploadedDocuments'] && this.uploadedDocuments) {
      this.syncUploadedDocuments();
    }
    if (!shouldHydrate && changes['lockRoles'] && this.lockRoles) {
      this.rows.forEach(row => {
        row.isGuarantor = true;
        row.isSigner = true;
      });
      this.emit();
    }
  }

  ngOnDestroy(): void {
    this.clearRows();
  }

  addPerson(): void {
    if (this.maxPeople != null && this.rows.length >= this.maxPeople) return;
    this.rows.push(this.createRow(null));
    this.emit();
  }

  removePerson(row: ManualPersonRow): void {
    row.subscription.unsubscribe();
    this.rows = this.rows.filter(candidate => candidate !== row);
    this.emit();
  }

  onRoleChange(): void {
    this.emit();
  }

  onIdDocumentDelete(row: ManualPersonRow, document: UploadedDocumentRef): void {
    row.uploadedIdDocuments = row.uploadedIdDocuments.filter(candidate => candidate.name !== document.name);
    row.idDocumentProvided = row.uploadedIdDocuments.length > 0 || !!row.control.value?.idDocument?.length;
    this.idDocumentDelete.emit(document);
    this.emit();
  }

  mark(): void {
    this.showValidation = true;
    this.rows.forEach(row => row.control.markAllAsTouched());
    this.emit();
  }

  idDocumentUploads(): IndividualIdDocumentUpload[] {
    return this.rows.flatMap(row => {
      const value = row.control.value;
      if (!value?.idDocument?.length) return [];
      return [{
        name: `${value.firstName ?? ''} ${value.lastName ?? ''}`.trim(),
        files: [...value.idDocument],
        individualId: row.clientId,
      }];
    });
  }

  private hydrate(): void {
    if (!this.commercialEntityId) return;
    this.clearRows();
    this.rows = (this.initialPeople ?? []).map(person => this.createRow(person));
    this.hydratedForEntityId = this.commercialEntityId;
    this.emit();
  }

  private clearRows(): void {
    this.rows.forEach(row => row.subscription.unsubscribe());
    this.rows = [];
  }

  private createRow(person: KybManualPerson | null): ManualPersonRow {
    const control = this.formBuilder.control<IndividualFormValue>(
      person ? this.toIndividualFormValue(person) : null,
    );
    const uploadedDocuments = person && this.uploadedDocuments
      ? this.documentsForPerson(person.clientId, person.individualId, person.givenName, person.surname)
      : [];
    const row: ManualPersonRow = {
      clientId: person?.clientId ?? v4(),
      individualId: person?.individualId ?? null,
      applicationIndividualRoleId: person?.applicationIndividualRoleId ?? null,
      control,
      isSigner: this.lockRoles || !!person?.isSigner,
      isGuarantor: this.lockRoles || !!person?.isGuarantor,
      idDocumentProvided: this.uploadedDocuments
        ? uploadedDocuments.length > 0
        : !!person?.idDocumentProvided,
      uploadedIdDocuments: uploadedDocuments,
      subscription: new Subscription(),
    };
    row.subscription = control.valueChanges.subscribe(() => this.emit());
    return row;
  }

  private syncUploadedDocuments(): void {
    this.rows.forEach(row => {
      const value = row.control.value;
      const documents = this.documentsForPerson(
        row.clientId,
        row.individualId,
        value?.firstName ?? '',
        value?.lastName ?? '',
      );
      row.uploadedIdDocuments = documents;
      row.idDocumentProvided = documents.length > 0 || !!value?.idDocument?.length;
    });
    this.emit();
  }

  private documentsForPerson(
    clientId: string,
    individualId: number | null,
    givenName: string,
    surname: string,
  ): AzureStorageDocument[] {
    return individualIdDocumentsOnFile(
      this.uploadedDocuments ?? [],
      [clientId, individualId],
      `${givenName} ${surname}`,
    );
  }

  private toIndividualFormValue(person: KybManualPerson): IndividualFormValue {
    return {
      title: person.title ? { type: person.title as any, name: person.title } : null,
      firstName: person.givenName,
      middleName: person.middleName ?? '',
      lastName: person.surname,
      dob: person.dateOfBirth ? moment(person.dateOfBirth) : null as any,
      hasAlias: person.hasAlias,
      aliasNames: person.aliasNames,
      countryOfCitizenship: countryOfCitizenshipByName(person.countryOfCitizenship),
      email: person.email,
      mobileNumber: person.mobileNumber,
      residentialAddress: person.residentialAddress,
      propertyOwner: person.propertyOwner ? {
        propertyOwner: true,
        propertyValue: person.propertyValue ?? undefined,
        mortgageValue: person.mortgageBalance ?? undefined,
        address: person.propertyOwnedAddress ?? undefined,
      } : null,
      identification: person.identificationType && person.identificationNumber
        ? { idType: person.identificationType, idNumber: person.identificationNumber }
        : null,
      idDocument: null,
    };
  }

  private toManualPerson(row: ManualPersonRow): KybManualPerson | null {
    const value = row.control.value;
    if (!value) return null;
    const property = value.propertyOwner && typeof value.propertyOwner === 'object'
      ? value.propertyOwner as Exclude<PropertyOwnerWithAddressValue, null>
      : null;
    return {
      clientId: row.clientId,
      individualId: row.individualId,
      applicationIndividualRoleId: row.applicationIndividualRoleId,
      commercialEntityId: this.commercialEntityId,
      title: value.title?.type ?? null,
      givenName: value.firstName ?? '',
      middleName: value.middleName || null,
      surname: value.lastName ?? '',
      dateOfBirth: value.dob?.format('YYYY-MM-DD') ?? '',
      email: value.email ?? '',
      mobileNumber: value.mobileNumber ?? '',
      hasAlias: !!value.hasAlias,
      aliasNames: value.hasAlias ? value.aliasNames ?? null : null,
      countryOfCitizenship: value.countryOfCitizenship?.name ?? null,
      residentialAddress: value.residentialAddress ?? null,
      propertyOwner: !!property?.propertyOwner,
      propertyValue: property?.propertyValue ?? null,
      mortgageBalance: property?.mortgageValue ?? null,
      propertyOwnedAddress: property?.address ?? null,
      identificationType: value.identification?.idType ?? null,
      identificationNumber: value.identification?.idNumber ?? null,
      idDocumentProvided: row.idDocumentProvided || !!value.idDocument?.length,
      isSigner: row.isSigner,
      isGuarantor: row.isGuarantor,
    };
  }

  private emit(): void {
    const people = this.rows
      .map(row => this.toManualPerson(row))
      .filter((person): person is KybManualPerson => !!person);
    const valid = this.rows.length > 0
      && this.rows.every(row => row.control.valid)
      && this.rows.some(row => row.isSigner);
    this.peopleChange.emit(people);
    this.validChange.emit(valid);
  }
}
